import React, { useCallback, useRef, useState } from 'react';
import { menuHandleOf, type ElementNodeDto } from '@xshop/shared';
import { useElementBox, useElementRender, type ElementComponent } from './elementRenderContext';
import { boolProp, choiceProp, textProp, type ElementProps } from './elementProps';
import { ELEMENT_FALLBACK_ICON, ELEMENT_ICONS } from './elementIcons';
import { MenuDropdown } from './MenuDropdown';
import { MenuDrawer } from './MenuDrawer';
import { menuLinks, type MenuLink } from './menuItems';
import type { MegaProducts } from './MenuMegaProducts';
import { useCloseOutside } from './useCloseOutside';
import { menuFoldOf, useMenuFold } from './useMenuFold';
import { useStoreMenu } from './useStoreMenu';
import './elementMenu.css';
import './elementMenuFold.css';

// The store's menu: a row of links, the drop-downs some of them open, and below a width the merchant
// chose the button they all fold behind — into a panel, or into the phone menu's drawer over the whole
// screen. The links come from one of the store's own menus, read by handle when
// `items` is bound to one, or from the list typed into the element; colour and type are the node's own
// compiled rule, which the whole menu inherits, so a merchant restyles it like a paragraph.

/** The shape the folded list opens in, exactly as `menuElement` offers it. */
const OPEN_AS = ['dropdown', 'drawer'] as const;

/** Whether a hover opens a drop-down on a desktop, exactly as `menuElement` offers it. */
const TRIGGERS = ['hover', 'click'] as const;

// Names the store menu the element's links are bound to, or nothing when they are the ones typed in.
const storeHandle = (node: ElementNodeDto): string | null => menuHandleOf(node.bindings?.items ?? '');

// Reports whether a menu stacks its links, as a footer's column does, rather than running them across.
const stacks = (node: ElementNodeDto): boolean => node.styles?.base?.default?.flexDirection === 'column';

// Reads the product row a menu's mega panels show, or null when the merchant has not turned it on.
const megaProducts = (props: ElementProps): MegaProducts | null =>
  (boolProp(props, 'megaProducts') ? { title: textProp(props, 'megaTitle', 'Trend this week'), handle: textProp(props, 'megaCollection') } : null);

/** What the row of top-level links draws with: the links, how their drop-downs open, and what the mega panels show. */
type MenuRowsProps = { links: readonly MenuLink[]; folded: boolean; hoverOpens: boolean; onLeave: () => void; products: MegaProducts | null; stacked: boolean };

// Draws the top-level links: a plain link, or a drop-down for one with links under it in a menu that runs across.
const MenuRows: React.FC<MenuRowsProps> = ({ links, folded, hoverOpens, onLeave, products, stacked }) => (
  <>
    {links.map((link) => (
      link.children.length > 0 && !stacked
        ? <MenuDropdown key={`${link.label}-${link.href ?? ''}`} link={link} folded={folded} hoverOpens={hoverOpens} onLeave={onLeave} products={products} />
        : (
          <li key={`${link.label}-${link.href ?? ''}`} className="xe-menu-item">
            <a className="xe-menu-link" href={link.href} onClick={onLeave} dir="auto">{link.label}</a>
          </li>
        )
    ))}
  </>
);

// Draws the menu landmark: the button that folds it, and the links and drop-downs it holds.
export const MenuElement: ElementComponent = ({ node }) => {
  const box = useElementBox(node);
  const { editing, store } = useElementRender();
  const handle = storeHandle(node);
  const stored = useStoreMenu(handle);
  const links = menuLinks(handle === null ? node.props.items : stored.items);
  const [open, setOpen] = useState(false);
  const host = useRef<HTMLElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useCloseOutside(host, open, close);
  const Burger = ELEMENT_ICONS.menu ?? ELEMENT_FALLBACK_ICON;
  const listId = `${node.domId ?? node.id}-menu`;
  const folded = useMenuFold(menuFoldOf(node.props.fold));
  const openAs = choiceProp(node.props, 'openAs', OPEN_AS, 'drawer');
  // A folded menu that opens as a drawer is Sleek's phone menu, its own dialog over the whole screen.
  const asDrawer = folded && openAs === 'drawer';

  return (
    <nav
      {...box}
      ref={host}
      className={[box.className, 'xe-menu'].filter(Boolean).join(' ')}
      aria-label={textProp(node.props, 'ariaLabel', 'Main')}
      data-folded={folded || undefined}
      data-open-as={openAs}
      data-stacked={stacks(node) || undefined}
    >
      <button
        type="button"
        className="xe-menu-burger"
        aria-expanded={open}
        aria-controls={asDrawer ? undefined : listId}
        aria-haspopup={asDrawer ? 'dialog' : undefined}
        aria-label={textProp(node.props, 'menuLabel', 'Menu')}
        onClick={() => setOpen((was) => !was)}
      >
        <Burger size={24} strokeWidth={1.5} aria-hidden="true" />
      </button>
      {asDrawer ? <MenuDrawer open={open} links={links} title={store?.storeName || 'Menu'} social={store?.social} onClose={close} /> : null}
      <ul id={listId} className="xe-menu-list" data-open={(open && !asDrawer) || undefined} hidden={asDrawer || undefined}>
        <MenuRows links={links} folded={open} hoverOpens={choiceProp(node.props, 'trigger', TRIGGERS, 'hover') === 'hover'} onLeave={close} products={megaProducts(node.props)} stacked={stacks(node)} />
      </ul>
      {/* A shopper sees nothing where a menu is missing; a merchant has to, or they cannot fix it. */}
      {editing && stored.status === 'missing' && (
        <span className="xe-menu-note">No menu called “{handle}” — make one under Online store › Navigation.</span>
      )}
    </nav>
  );
};
